import React from 'react';
import {
  Chip,
  Avatar as MUIAvatar,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ICourseData } from '../../types/course-data';
import Avatar from '../common/avatar';
import { LevelIconSelector } from '../../utils/select-level-icon';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';

interface MetadataItemProps {
  label: string;
  children: React.ReactNode;
}

const MetadataItem: React.FC<MetadataItemProps> = ({ label, children }) => {
  return (
    <div className="flex flex-col items-start w-full gap-2">
      <Typography variant="body-small-500" className="text-text-secondary" component="p">
        {label}
      </Typography>
      {children}
    </div>
  );
};

// Define a more complete interface that extends Partial<ICourseData>
export interface ModalMetadataProps extends Partial<ICourseData> {
  isFavourite?: boolean;
  isLoading?: boolean;
  toggleFavourite?: () => void;
  isLoggedIn?: boolean;
}

export const ModalMetadata: React.FC<ModalMetadataProps> = ({
  videoCount,
  level,
  genres,
  duration,
  instructors,
  courseType,
  highlighted,
  isFavourite,
  isLoading,
  requirements,
  toggleFavourite,
  isLoggedIn,
}) => {
  // Check if this is a series course type (favorites not allowed for series)
  const isSerie = courseType?.toLowerCase() === 'serie';
  // Show favorite button only for non-series courses
  const showFavoriteButton = !isSerie && toggleFavourite;

  return (
    <div className="flex flex-col items-start gap-4">
      {isLoggedIn && showFavoriteButton && (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className="w-full"
          onClick={toggleFavourite}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : isFavourite ? (
              <FavoriteIcon color="primary" className="size-4" />
            ) : (
              <FavoriteBorderIcon className="size-4" />
            )
          }
        >
          <Typography variant="button-s">
            {isFavourite
              ? 'Aggiunto ai preferiti'
              : isLoading
                ? 'In elaborazione...'
                : 'Aggiungi a preferiti'}
          </Typography>
        </Button>
      )}
      {courseType?.toLowerCase() !== 'masterclass' && (
        <MetadataItem label="Numero lezioni">
          {videoCount !== undefined && (
            <Chip
              label={
                <Typography variant="button-s">
                  {videoCount} lezion{videoCount !== 1 ? 'i' : 'e'}
                </Typography>
              }
              size="medium"
              avatar={<MUIAvatar src="/assets/images/play-arrow-filled.png" className="mr-0" />}
              sx={{
                background: '#54566870',
              }}
            />
          )}
        </MetadataItem>
      )}
      <MetadataItem label="Livello">
        <Chip
          label={<Typography variant="button-s">{level}</Typography>}
          size="medium"
          avatar={
            <MUIAvatar
              src={LevelIconSelector(level, courseType as any, highlighted)}
              className="mr-0"
              sx={{ borderRadius: 0 }}
            />
          }
          sx={{
            background: '#54566870',
          }}
        />
      </MetadataItem>
      {requirements && (
        <MetadataItem label={'Requisiti'}>
          <Typography variant="caption-500" className="text-text-primary">
            {requirements}
          </Typography>
        </MetadataItem>
      )}
      {Array.isArray(genres) && genres.length > 0 && (
        <MetadataItem label="Genere">
          <span className="space-x-2">
            {genres.map((genre, index) => (
              <Chip
                key={index}
                label={<Typography variant="button-s">{genre}</Typography>}
                size="medium"
                color="default"
                variant="outlined"
                className="self-start"
              />
            ))}
          </span>
        </MetadataItem>
      )}
      <MetadataItem label={`Durata ${courseType ? adaptCourseTypeText(courseType) : ''}`}>
        <Typography variant="caption-500" className="text-text-primary">
          {duration ? Math.round(duration / 60) : 0} min
        </Typography>
      </MetadataItem>
      <MetadataItem label="Insegnante">
        <div className="flex items-center text-text-primary">
          {Array.isArray(instructors) && instructors.length > 1 ? (
            <Tooltip
              placement="top-start"
              arrow
              title={instructors.map((instructor) => instructor.name).join(', ')}
            >
              <span>
                <Avatar instructors={instructors} courseType={courseType} />
              </span>
            </Tooltip>
          ) : (
            <Avatar
              instructors={instructors as ICourseData['instructors']}
              courseType={courseType}
            />
          )}
        </div>
      </MetadataItem>
    </div>
  );
};
